import { SxProps } from '@mui/material';
import { AlertCard } from 'common/ui';
import { LoadingIndicator } from 'common/ui/LoadingIndicator';
import { CandidateBatchStatus } from 'generated-graphql/types';
import { getAnalyseDetailsStatusText } from 'modules/analyse/utils/getAnalyseDetailsStatusText';
import { isAnalyseProcessing } from 'modules/analyse/utils/isAnalyseProcessing';

type Props = {
  status?: CandidateBatchStatus;
  isRecalculatingScores: boolean;
  candidatesRemainingCount: number;
  archivedCampaignNames?: string[];
};

const alertSx: SxProps = {
  maxWidth: '600px',
  mx: 'auto',
  mb: '20px'
};

export const AnalysisAlert: React.FC<Props> = ({
  status,
  isRecalculatingScores,
  candidatesRemainingCount,
  archivedCampaignNames = []
}) => {
  const renderStatusAlert = () => {
    if (status === CandidateBatchStatus.Archived) {
      return (
        <AlertCard
          type="warning"
          title="This candidate batch is archived"
          sx={alertSx}
        >
          This analysis has been archived and no longer active
        </AlertCard>
      );
    }

    if (isRecalculatingScores) {
      return (
        <AlertCard
          type="info"
          title={`Re-analysing candidates`}
          sx={alertSx}
          isLoading
        >
          This may take a minute or two
        </AlertCard>
      );
    }

    if (
      status === CandidateBatchStatus.RankingCandidates &&
      candidatesRemainingCount > 0
    ) {
      return (
        <AlertCard
          type="info"
          title={`${candidatesRemainingCount} candidate${
            candidatesRemainingCount > 1 ? 's' : ''
          } left to analyse`}
          sx={alertSx}
          isLoading
        />
      );
    }

    if (status && isAnalyseProcessing(status)) {
      return (
        <LoadingIndicator
          stackSx={{
            py: '20px'
          }}
          title={getAnalyseDetailsStatusText(status)}
        />
      );
    }

    return null;
  };

  return (
    <>
      {archivedCampaignNames.length > 0 && (
        <AlertCard
          type="warning"
          title={`The linked campaign${
            archivedCampaignNames.length > 1 ? 's' : ''
          } ${archivedCampaignNames.join(', ')} ${
            archivedCampaignNames.length > 1 ? 'have' : 'has'
          } been archived`}
          sx={alertSx}
        >
          Choose another campaign to send to applicants, or remove the action.
        </AlertCard>
      )}
      {renderStatusAlert()}
    </>
  );
};
